import { create } from 'zustand'
import authClient from '../lib/auth'
import type { User } from '../lib/auth'

interface AuthState {
  user: User | null
  loading: boolean
  initialized: boolean
  signInWithGoogle: (returnPath?: string) => Promise<void>
  loginWithEmail: (email: string, password: string) => Promise<void>
  registerWithEmail: (email: string, password: string) => Promise<{ requiresVerification: boolean; email: string }>
  verifyOtpAndLogin: (email: string, otp: string) => Promise<void>
  signOut: () => Promise<void>
  initializeAuth: () => void
  handleOAuthCallback: () => Promise<void>
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  initialized: false,

  initializeAuth: () => {
    if (useAuthStore.getState().initialized) return
    set({ initialized: true })
    if (authClient.isAuthenticated()) {
      set({ user: authClient.getUser(), loading: false })
    } else {
      set({ user: null, loading: false })
    }
  },

  signInWithGoogle: async (returnPath?: string) => {
    set({ loading: true })
    try {
      const url = await authClient.getGoogleOAuthUrl(returnPath)
      window.location.href = url
    } catch (e: unknown) {
      console.error('Google sign-in failed:', e)
      set({ loading: false })
      throw e
    }
  },

  loginWithEmail: async (email: string, password: string) => {
    set({ loading: true })
    try {
      await authClient.login(email, password)
      const user = authClient.getUser()
      set({ user, loading: false })
    } catch (e: unknown) {
      set({ loading: false })
      throw e
    }
  },

  registerWithEmail: async (email: string, password: string) => {
    set({ loading: true })
    try {
      const result = await authClient.register(email, password)
      set({ loading: false })
      return result
    } catch (e: unknown) {
      set({ loading: false })
      throw e
    }
  },

  verifyOtpAndLogin: async (email: string, otp: string) => {
    set({ loading: true })
    try {
      await authClient.verifyOtpAndLogin(email, otp)
      const user = authClient.getUser()
      set({ user, loading: false })
    } catch (e: unknown) {
      set({ loading: false })
      throw e
    }
  },

  handleOAuthCallback: async () => {
    set({ loading: true })
    try {
      await authClient.handleOAuthCallback()
      const user = authClient.getUser()
      set({ user, loading: false })
    } catch (e: unknown) {
      console.error('OAuth callback failed:', e)
      set({ user: null, loading: false })
      throw e
    }
  },

  signOut: async () => {
    authClient.signOut()
    window.location.href = '/'
  },
}))
